import type { ClientPagination } from './pagination';
import type { Property } from '~/core/entities/property';

interface PropertyReturn {
	properties: ComputedRef<Property[]>;
	addProperties(properties: Property[]): void;
	needMoreProperties(): boolean;
	movePropertiesFromCached(): void;
	reset(): void;
}

/**
 * Represents a computed property that tracks pagination state.
 * @param pagination - The pagination data for managing properties.
 * @returns - Exposes properties and methods for managing property state.
 */
export function Property(
	pagination: ComputedRef<ClientPagination>,
	perPageFirstPage: number = 12,
	perPageNextPages: number = 12
): PropertyReturn {
	/**
	 * State for storing properties and cached properties persisted between server and client.
	 * Uses a stable key to ensure proper hydration - the state from SSR must be found by the client.
	 * Previously used a dynamic key based on favoritesCookie which caused hydration mismatches
	 * when the cookie value differed between SSR and client.
	 */
	const _propertiesState = useState<{
		_properties: Property[];
		_cachedProperties: Property[];
	}>('_properties-listing', () => ({
		_properties: [],
		_cachedProperties: []
	}));
	/*
	 * The properties computed value returns the current properties.
	 */
	const properties = computed(() => _propertiesState.value._properties);
	/**
	 * Sets the cached properties.
	 * @private
	 * @param properties - The properties to set as cached.
	 */
	function _setCachedProperties(properties: Property[]): void {
		_propertiesState.value._cachedProperties = properties;
	}
	/**
	 * Resets the cached properties
	 * @private
	 */
	function _resetCachedProperties(): void {
		_propertiesState.value._cachedProperties = [];
	}
	/**
	 * Sets the properties ensuring no duplicates based on property ID.
	 * @private
	 * @param properties - The properties to set.
	 */
	function _setProperties(properties: Property[]): void {
		const uniqueIds = new Set<string | number>();
		const uniqueProperties = properties.filter(property => {
			if (uniqueIds.has(property.id)) {
				return false;
			}
			uniqueIds.add(property.id);
			return true;
		});
		_propertiesState.value._properties = uniqueProperties;
	}
	/**
	 * Resets the properties.
	 * @private
	 */
	function _resetProperties(): void {
		_propertiesState.value._properties = [];
	}
	/**
	 * Checks if the current page is the first page.
	 * @returns - Whether the current page is the first page.
	 */
	function _isFirstPage(): boolean {
		return pagination.value.page === 1;
	}
	/**
	 * Checks if more properties are needed to be fetched.
	 * @returns - Whether more properties are needed to be fetched.
	 */
	function needMoreProperties(): boolean {
		const hasLessCachedPropertiesThanNeeded =
			_propertiesState.value._cachedProperties.length <
			pagination.value.perPage;

		const hasLessStoredPropertiesThanAvailable =
			_propertiesState.value._cachedProperties.length +
				_propertiesState.value._properties.length <
			pagination.value.filteredCount;

		const hasMorePages = pagination.value.page < pagination.value.totalPages;

		return (
			hasMorePages &&
			(hasLessCachedPropertiesThanNeeded ||
				hasLessStoredPropertiesThanAvailable)
		);
	}
	/**
	 * Adds properties to the current properties.
	 * @param properties - The properties to add.
	 */
	function addProperties(properties: Property[]): void {
		const perPage = _isFirstPage() ? perPageFirstPage : perPageNextPages;

		if (import.meta.server) {
			_setCachedProperties(properties);
			_setProperties(properties);
			return;
		}

		const newProperties = _isFirstPage()
			? properties.slice(0, perPage)
			: [
					..._propertiesState.value._properties,
					...properties.slice(0, perPage)
				];

		const newCachedProperties = _isFirstPage()
			? properties.slice(perPage)
			: properties.slice(perPage);

		_setCachedProperties(newCachedProperties);
		_setProperties(newProperties);
	}
	/**
	 * Moves properties from cached properties to properties.
	 * This should be user for client next page navigation.
	 */
	function movePropertiesFromCached(): void {
		const newProperties: Property[] = [
			..._propertiesState.value._properties,
			..._propertiesState.value._cachedProperties.slice(0, perPageNextPages)
		];

		const newCachedProperties: Property[] =
			_propertiesState.value._cachedProperties.slice(perPageNextPages);

		_setProperties(newProperties);
		_setCachedProperties(newCachedProperties);
	}
	/**
	 * Resets the properties and cached properties.
	 */
	function reset(): void {
		_resetProperties();
		_resetCachedProperties();
	}

	return {
		properties,
		addProperties,
		needMoreProperties,
		movePropertiesFromCached,
		reset
	};
}
